import { useEffect, useState } from "react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { toast } from "sonner";
import {
  DollarSign, ShoppingCart, TrendingUp, Calendar,
  Package, AlertCircle, Trash2, RefreshCw,
} from "lucide-react";

const STATUS_LABEL = {
  pending: "Pending payment",
  paid: "Paid",
  ordered: "Ordered",
  ready_for_pickup: "Ready for Pickup",
  ready_for_delivery: "Ready for Delivery",
  completed: "Completed",
};

const STATUS_COLOR = {
  pending: "#fef3c7|#92400e",
  paid: "#dcfce7|#166534",
  ordered: "#dbeafe|#1e40af",
  ready_for_pickup: "#fef9c3|#854d0e",
  ready_for_delivery: "#e0e7ff|#3730a3",
  completed: "#d1fae5|#065f46",
};

function money(n) {
  return `$${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}

function StatCard({ label, value, hint, Icon }) {
  return (
    <div data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="bg-white rounded-2xl border border-[var(--brand-border)] p-5">
      <div className="flex items-start justify-between mb-1">
        <p className="text-[11px] uppercase tracking-wider muted-text font-bold">{label}</p>
        <Icon className="w-4 h-4 muted-text shrink-0" />
      </div>
      <p className="text-3xl font-extrabold text-[var(--brand-text)]">{value}</p>
      {hint && <p className="text-xs muted-text mt-1">{hint}</p>}
    </div>
  );
}

function Sparkline({ daily }) {
  // Tiny, library-free SVG sparkline. Auto-scales on max revenue across the
  // 30-day window so a quiet day still shows on the baseline (1px).
  const W = 800;   // intrinsic — scaled via viewBox on the parent
  const H = 80;
  const PAD = 4;
  const max = Math.max(1, ...daily.map((d) => d.revenue));
  const xStep = (W - PAD * 2) / Math.max(1, daily.length - 1);
  const points = daily.map((d, i) => {
    const x = PAD + i * xStep;
    const y = H - PAD - (d.revenue / max) * (H - PAD * 2);
    return [x, y, d];
  });
  const path = points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  // Filled area below the line for visual weight.
  const area =
    `M${PAD},${H - PAD} ` +
    points.map(([x, y]) => `L${x.toFixed(1)},${y.toFixed(1)}`).join(" ") +
    ` L${W - PAD},${H - PAD} Z`;
  const lastValue = daily[daily.length - 1]?.revenue || 0;
  return (
    <div data-testid="revenue-sparkline" className="bg-white rounded-2xl border border-[var(--brand-border)] p-5">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-bold uppercase tracking-wider muted-text">Revenue · last 30 days</h2>
        <span className="text-xs muted-text">Today {money(lastValue)}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
        className="w-full h-20 block" aria-hidden="true">
        <defs>
          <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1f7a3d" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#1f7a3d" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#sparkFill)" />
        <path d={path} fill="none" stroke="#1f7a3d" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" />
        {points.map(([x, y, d], i) => (
          <circle key={i} cx={x} cy={y} r={d.revenue > 0 ? 3 : 1.5}
            fill={d.revenue > 0 ? "#1f7a3d" : "#d1d5db"}>
            <title>{`${d.date}: ${money(d.revenue)} · ${d.count} order${d.count === 1 ? "" : "s"}`}</title>
          </circle>
        ))}
      </svg>
      <div className="flex justify-between text-[10px] muted-text mt-1 px-1">
        <span>{daily[0]?.date}</span>
        <span>{daily[Math.floor(daily.length / 2)]?.date}</span>
        <span>{daily[daily.length - 1]?.date}</span>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cleaning, setCleaning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/kpis");
      setData(r.data);
    } catch {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const cleanupTest = async () => {
    if (!window.confirm(
      "Delete all test/dummy orders (anything from @example.com, @test.com, etc.)? This cannot be undone."
    )) return;
    setCleaning(true);
    try {
      const r = await api.post("/admin/maintenance/cleanup-test-orders");
      toast.success(`Deleted ${r.data.deleted} test orders`);
      load();
    } catch {
      toast.error("Cleanup failed");
    } finally {
      setCleaning(false);
    }
  };

  if (loading || !data) {
    return (
      <AdminLayout>
        <p className="muted-text">Loading dashboard…</p>
      </AdminLayout>
    );
  }

  const { revenue, profit: profitData, orders, products, pro_measurement_pending, awaiting_measurements, low_stock_catalog, upsells } = data;
  const conv = (orders.conversion_rate * 100).toFixed(1);
  const marginPct = (profitData.margin * 100).toFixed(1);
  const takeRatePct = upsells ? (upsells.take_rate * 100).toFixed(1) : "0.0";

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-2xl font-bold text-[var(--brand-text)]">Dashboard</h1>
        <div className="flex gap-2">
          <button data-testid="dashboard-refresh-btn" onClick={load}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold border-2 border-[var(--brand-border)] hover:brand-text">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button data-testid="cleanup-test-orders-btn" onClick={cleanupTest} disabled={cleaning}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold border-2 border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50">
            <Trash2 className="w-3.5 h-3.5" /> {cleaning ? "Cleaning…" : "Clean test orders"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Total Revenue" value={money(revenue.total)}
          hint={`${orders.paid} paid orders`} Icon={DollarSign} />
        <StatCard label="Today" value={money(revenue.today)} Icon={Calendar} />
        <StatCard label="Last 7 Days" value={money(revenue.last_7d)} Icon={TrendingUp} />
        <StatCard label="Last 30 Days" value={money(revenue.last_30d)} Icon={TrendingUp} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Profit" value={money(profitData.profit_total)}
          hint={`${marginPct}% margin · ${profitData.costed_orders}/${orders.paid} costed`} Icon={DollarSign} />
        <StatCard label="Cost of Goods" value={money(profitData.cost_total)}
          hint={profitData.uncosted_orders ? `${profitData.uncosted_orders} order${profitData.uncosted_orders === 1 ? "" : "s"} missing cost` : "All paid orders costed"} Icon={Package} />
        <StatCard label="Avg Order Value" value={money(revenue.average_order_value)} Icon={ShoppingCart} />
        <StatCard label="Conversion" value={`${conv}%`}
          hint={`${orders.paid}/${orders.total} carts paid`} Icon={TrendingUp} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Awaiting Measurements" value={awaiting_measurements}
          hint="Paid but no measurements yet" Icon={AlertCircle} />
        <StatCard label="Pro Measure Pending" value={pro_measurement_pending}
          hint="Waiting to schedule" Icon={Calendar} />
        <StatCard label="Upsell take-rate" value={`${takeRatePct}%`}
          hint={upsells ? `${upsells.orders_with_upsell}/${upsells.funnel_paid_orders} orders bought add-ons` : "—"}
          Icon={TrendingUp} />
        <StatCard label="Add-on Revenue" value={money(upsells?.addon_gross_revenue || 0)}
          hint={upsells ? `${upsells.addon_charge_count} charges · avg ${money(upsells.avg_addon_value)}` : ""}
          Icon={DollarSign} />
      </div>

      {revenue.daily?.length > 0 && (
        <div className="mb-5">
          <Sparkline daily={revenue.daily} />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-[var(--brand-border)] p-5">
          <div className="flex items-center gap-2 mb-3">
            <ShoppingCart className="w-4 h-4 brand-text" />
            <h2 className="text-sm font-bold uppercase tracking-wider muted-text">Orders by status</h2>
          </div>
          {orders.total === 0 ? (
            <p className="text-sm muted-text">No orders yet.</p>
          ) : (
            <ul className="space-y-2">
              {Object.entries(orders.by_status).map(([s, c]) => {
                const [bg, fg] = (STATUS_COLOR[s] || "#e5e7eb|#374151").split("|");
                const pct = ((c / orders.total) * 100).toFixed(0);
                return (
                  <li key={s} data-testid={`status-row-${s}`}
                    className="flex items-center justify-between gap-3">
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wider"
                      style={{ background: bg, color: fg }}>
                      {STATUS_LABEL[s] || s.replace(/_/g, " ")}
                    </span>
                    <div className="flex-1 mx-2 h-1.5 rounded-full bg-[var(--brand-border)] overflow-hidden">
                      <div className="h-full" style={{ width: `${pct}%`, background: fg }} />
                    </div>
                    <span className="text-sm font-semibold text-[var(--brand-text)] tabular-nums w-10 text-right">
                      {c}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-[var(--brand-border)] p-5">
          <div className="flex items-center gap-2 mb-3">
            <Package className="w-4 h-4 brand-text" />
            <h2 className="text-sm font-bold uppercase tracking-wider muted-text">Top products</h2>
          </div>
          {products.top_by_qty.length === 0 ? (
            <p className="text-sm muted-text">No paid sales yet.</p>
          ) : (
            <ul className="space-y-2">
              {products.top_by_qty.map((p) => (
                <li key={p.slug} data-testid={`top-product-${p.slug}`}
                  className="flex items-center justify-between gap-3 text-sm">
                  <span className="font-semibold text-[var(--brand-text)] truncate">{p.name}</span>
                  <span className="muted-text tabular-nums">×{p.qty}</span>
                  <span className="font-semibold brand-text tabular-nums w-20 text-right">{money(p.revenue)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <p className="text-[11px] muted-text mt-4">As of {new Date(data.as_of).toLocaleString()}</p>

      {low_stock_catalog && low_stock_catalog.length > 0 && (
        <div data-testid="low-stock-widget"
          className="mt-5 bg-white rounded-2xl border-2 border-amber-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-amber-900">Low stock — reorder soon</h2>
          </div>
          <ul className="space-y-2">
            {low_stock_catalog.map((it) => (
              <li key={it.slug} data-testid={`low-stock-${it.slug}`}
                className="flex items-center justify-between gap-3 text-sm">
                <span className="font-semibold text-[var(--brand-text)] truncate">{it.name}</span>
                <span className="muted-text capitalize">{it.category}</span>
                <span className="font-extrabold text-amber-700 tabular-nums w-12 text-right">
                  {it.stock_count} left
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </AdminLayout>
  );
}
